
import { useState, useCallback, useEffect } from 'react';

export const useShopSelection = () => {
  const [selectedShops, setSelectedShops] = useState<string[]>([]);
  const [singleSelectedShop, setSingleSelectedShop] = useState<string>('');
  const [isSingleSelectionMode, setIsSingleSelectionMode] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize with any saved shop ID, but only once
  useEffect(() => {
    if (!isInitialized) {
      const savedShopId = localStorage.getItem('selectedShopId');
      if (savedShopId) {
        console.log('Initializing selection with saved shop ID:', savedShopId);
        setSingleSelectedShop(savedShopId);
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Custom setter for singleSelectedShop that also updates localStorage
  const setSelectedShopWithStorage = useCallback((id: string) => {
    setSingleSelectedShop(id);
    localStorage.setItem('selectedShopId', id);
    console.log('Shop selection set to:', id);
  }, []);

  const toggleShopSelection = (id: string) => {
    console.log('Toggle shop selection for:', id);
    
    // If we are in single selection mode, update singleSelectedShop
    if (isSingleSelectionMode) {
      // Only update if it's a different shop than currently selected
      if (singleSelectedShop !== id) {
        setSelectedShopWithStorage(id);
      }
      return;
    }
    
    // In multi-selection mode
    setSelectedShops(prev => 
      prev.includes(id) 
        ? prev.filter(shopId => shopId !== id) 
        : [...prev, id]
    );
  };

  const toggleSelectionMode = useCallback(() => {
    setIsSingleSelectionMode(prev => !prev);
  }, []);

  const getSelectedShopIds = useCallback(() => {
    if (isSingleSelectionMode && singleSelectedShop) {
      return [singleSelectedShop];
    }
    return selectedShops;
  }, [isSingleSelectionMode, singleSelectedShop, selectedShops]);

  const clearFilters = useCallback(() => {
    setSelectedShops([]);
    setSingleSelectedShop('');
    localStorage.removeItem('selectedShopId');
  }, []);

  return {
    selectedShops,
    singleSelectedShop,
    setSingleSelectedShop: setSelectedShopWithStorage,
    isSingleSelectionMode,
    toggleShopSelection,
    toggleSelectionMode,
    getSelectedShopIds,
    clearFilters,
    isInitialized
  };
};
